import { DateTime } from "luxon";
import IconDone from "../../components/icons/IconDone";
import IconWrong from "../../components/icons/IconWrong";
import IconEdit from "../../components/icons/IconEdit";
import BotErrorBadge from "../../components/botHealth/BotErrorBadge";
import useMessagesTimeline from "./useMessagesTimeline";

function formatTime(value) {
    if (!value) return "";
    const cleaned = String(value).replace(/\[.*\]/, "");
    const dt = DateTime.fromISO(cleaned);
    return dt.isValid ? dt.toFormat("dd.MM.yyyy HH:mm") : value;
}

function formatResponseTime(seconds) {
    if (seconds == null) return "";
    if (seconds < 60) return `${seconds}с`;
    const minutes = Math.floor(seconds / 60);
    const rest = seconds % 60;
    return `${minutes}хв ${rest}с`;
}

export default function MessagesTimeline({ conversationProgressId, conversation, outerUserId }) {
    const { messages, status } = useMessagesTimeline(conversationProgressId, conversation?.id, outerUserId);

    if (status === "loading") return <p className="conv-dashboard-empty">Завантаження...</p>;
    if (status === "error") return <p className="conv-dashboard-empty">Не вдалося завантажити повідомлення</p>;
    if (!messages || messages.length === 0) return <p className="conv-dashboard-empty">Повідомлень ще немає</p>;

    // Driven by what each row's connector actually carries rather than the
    // conversation's estimationFunction alone: that flag is often null even
    // on conversations with real isRight/estimationGroup/stage data on their
    // connectors (the backend's own stats fallback does the same thing -
    // see MessageService.estimationFunctionGetCountByWrongRight). Point is
    // the exception, since 0 is a valid but ambiguous value on every
    // connector - it's only shown for conversations that are actually
    // point-scored.
    const showPoint = conversation?.estimationFunction?.id === 3;

    return (
        <ul className="messages-timeline">
            {messages.map((message) => {
                const connector = message.answerConnector;
                const isBot = message.isBot === 1;
                return (
                    <li key={message.id} className={"messages-timeline-row " + (isBot ? "messages-timeline-row-bot" : "messages-timeline-row-user")}>
                        <div className="messages-timeline-bubble">
                            <div className="messages-timeline-bubble-header">
                                <div className="messages-timeline-header-left">
                                    <span className="messages-timeline-sender">{isBot ? "Бот" : "Учень"}</span>
                                    {isBot && message.questionTotal > 1 ? (
                                        <span className="messages-timeline-question-index">
                                            Питання {message.questionIndex}/{message.questionTotal}
                                        </span>
                                    ) : (
                                        ""
                                    )}
                                </div>
                                {message.errors ? <BotErrorBadge errors={message.errors} /> : ""}
                            </div>
                            <p className="messages-timeline-text">{message.text}</p>
                            <div className="messages-timeline-meta">
                                {connector?.isRight ? (
                                    <span className={"messages-timeline-badge " + (connector.isRight.id === 1 ? "messages-timeline-badge-right" : "messages-timeline-badge-wrong")}>
                                        {connector.isRight.id === 1 ? (
                                            <IconDone fillColor="icon-green" style={{ height: 14, width: 14 }} />
                                        ) : (
                                            <IconWrong fillColor="icon-red" style={{ height: 14, width: 14 }} />
                                        )}
                                        {connector.isRight.name}
                                    </span>
                                ) : (
                                    ""
                                )}
                                {connector?.estimationGroup ? (
                                    <span className="messages-timeline-badge messages-timeline-badge-group">{connector.estimationGroup.name}</span>
                                ) : (
                                    ""
                                )}
                                {showPoint && connector ? (
                                    <span className="messages-timeline-badge messages-timeline-badge-point">
                                        {connector.point > 0 ? "+" : ""}
                                        {connector.point} балів
                                    </span>
                                ) : (
                                    ""
                                )}
                                {connector?.stage ? (
                                    <span
                                        className="messages-timeline-badge messages-timeline-badge-stage"
                                        style={{ borderLeft: "3px solid " + (connector.stage.color || "#7f7f7f") }}
                                    >
                                        {connector.stage.name}
                                    </span>
                                ) : (
                                    ""
                                )}
                                {!isBot && message.responseSeconds != null ? (
                                    <span className="messages-timeline-badge messages-timeline-badge-response">
                                        відповів за {formatResponseTime(message.responseSeconds)}
                                    </span>
                                ) : (
                                    ""
                                )}
                                <span className="messages-timeline-time">{formatTime(message.dateTime)}</span>
                            </div>
                        </div>
                        {isBot && message.questionOptions ? (
                            <div className="messages-timeline-options">
                                {message.isManualEntry ? (
                                    <>
                                        <span className="messages-timeline-option-chip messages-timeline-option-chip-manual">
                                            <IconEdit fillColor="icon-contrast" style={{ height: 12, width: 12, margin: 0 }} />
                                            <span>вільний ввід</span>
                                        </span>
                                        {message.questionOptions.map((option) => {
                                            const hasText = option.text && option.text.trim();
                                            return (
                                                <span
                                                    key={option.id}
                                                    className={
                                                        "messages-timeline-option-chip messages-timeline-option-chip-text " +
                                                        (option.isRight?.id === 1
                                                            ? "messages-timeline-option-chip-right"
                                                            : "messages-timeline-option-chip-other")
                                                    }
                                                >
                                                    {hasText ? option.text : <em>будь-який текст</em>}
                                                </span>
                                            );
                                        })}
                                    </>
                                ) : (
                                    message.questionOptions.map((option) => (
                                        <span
                                            key={option.id}
                                            className={
                                                "messages-timeline-option-chip " +
                                                (option.isRight?.id === 1
                                                    ? "messages-timeline-option-chip-right"
                                                    : "messages-timeline-option-chip-other")
                                            }
                                        >
                                            {option.text}
                                        </span>
                                    ))
                                )}
                            </div>
                        ) : (
                            ""
                        )}
                    </li>
                );
            })}
        </ul>
    );
}
